import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookingsByUserId } from "../../State/Booking/Action";
import moment from "moment";
import UpdateBookingDialog from "./UpdateBookingDialog";
import CancellationModal from "../Tools/CancellationModal";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import EventIcon from "@mui/icons-material/Event";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DoneIcon from "@mui/icons-material/Done";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import AppPagination from "../Tools/AppPagination";
import { getWashTypeLabel } from "../../constants/washTypes";

export default function MyBookings() {
  const dispatch = useDispatch();
  const { booking } = useSelector((store) => store);
  const [page, setPage] = useState(0);
  const [vehicleType, setVehicleType] = useState("CAR");

  useEffect(() => {
    const filter = `?vehicleType=${vehicleType}&page=${page}`;
    dispatch(getAllBookingsByUserId(filter));
  }, [dispatch, vehicleType, page]);

  const getStatusStyles = (status) => {
    switch (status) {
      case "BOOKED":
        return "bg-blue-400 text-blue-900";
      case "COMPLETED":
        return "bg-green-400 text-green-900";
      case "CANCELLED":
        return "bg-red-400 text-red-900";
      case "PENDING":
        return "bg-yellow-400 text-yellow-900";
      default:
        return "bg-gray-400 text-gray-900";
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-10 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="text-center mb-10">
        <h1 className="font-extrabold text-4xl text-gray-900 mb-3 tracking-tight">
          My Bookings
        </h1>
        <p className="text-gray-600 text-lg">
          Track and manage your vehicle wash appointments
        </p>
      </div>

      {/* Filter Icons */}
      <div className="flex justify-center gap-10 mb-16">
        <div className="flex flex-col items-center gap-2">
          <Tooltip title="Show car wash bookings" arrow>
            <IconButton
              onClick={() => setVehicleType("CAR")}
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                background:
                  vehicleType === "CAR"
                    ? "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)",
                color: vehicleType === "CAR" ? "#fff" : "#4f46e5",
                boxShadow:
                  vehicleType === "CAR"
                    ? "0 12px 25px -8px rgba(79,70,229,0.55)"
                    : "0 8px 20px -10px rgba(79,70,229,0.35)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                border: vehicleType === "CAR" ? "none" : "1px solid rgba(99,102,241,0.2)",
                "&:hover": {
                  transform: "translateY(-3px) scale(1.05)",
                  boxShadow:
                    vehicleType === "CAR"
                      ? "0 16px 32px -10px rgba(79,70,229,0.55)"
                      : "0 14px 25px -12px rgba(79,70,229,0.35)",
                },
              }}
            >
              <DirectionsCarIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <span
            className={`text-sm font-semibold ${
              vehicleType === "CAR" ? "text-indigo-600" : "text-gray-500"
            }`}
          >
            Car
          </span>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Tooltip title="Show bike wash bookings" arrow>
            <IconButton
              onClick={() => setVehicleType("BIKE")}
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                background:
                  vehicleType === "BIKE"
                    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
                    : "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.65) 100%)",
                color: vehicleType === "BIKE" ? "#fff" : "#0ea5e9",
                boxShadow:
                  vehicleType === "BIKE"
                    ? "0 12px 25px -8px rgba(14,165,233,0.55)"
                    : "0 8px 20px -10px rgba(14,165,233,0.35)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s ease",
                border: vehicleType === "BIKE" ? "none" : "1px solid rgba(14,165,233,0.2)",
                "&:hover": {
                  transform: "translateY(-3px) scale(1.05)",
                  boxShadow:
                    vehicleType === "BIKE"
                      ? "0 16px 32px -10px rgba(14,165,233,0.55)"
                      : "0 14px 25px -12px rgba(14,165,233,0.35)",
                },
              }}
            >
              <TwoWheelerIcon fontSize="medium" />
            </IconButton>
          </Tooltip>
          <span
            className={`text-sm font-semibold ${
              vehicleType === "BIKE" ? "text-sky-600" : "text-gray-500"
            }`}
          >
            Bike
          </span>
        </div>
      </div>

      {/* Booking List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mt-8 mx-auto max-w-7xl px-4">
        {booking.pagedBooking?.bookings?.map((bookingItem) => (
          <Card
            key={bookingItem.bookingId}
            className="group relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-3xl bg-white/90 backdrop-blur-sm w-full"
            sx={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.15)'
              }
            }}
          >
            <CardContent className="p-6">
              {/* Top accent bar */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
              
              <Box className="flex items-center justify-between mb-6">
                <Box className="flex items-center gap-3">
                  <Box className="p-2 rounded-full bg-blue-100">
                    {bookingItem.vehicle === "CAR" ? (
                      <DirectionsCarIcon className="text-blue-600" />
                    ) : (
                      <TwoWheelerIcon className="text-blue-600" />
                    )}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      className="font-bold text-gray-800 text-lg"
                    >
          {bookingItem.vehicle} - {getWashTypeLabel(bookingItem.type)}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      {bookingItem.seater}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusStyles(
                    bookingItem.status
                  )} shadow-sm`}
                >
                  {bookingItem.status}
                </Box>
              </Box>

              <Box className="space-y-4 mb-6">
                <Box className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <ConfirmationNumberIcon className="text-blue-500" fontSize="small" />
                  <Box>
                    <Typography variant="caption" className="text-gray-500 font-medium">
                      Booking ID
                    </Typography>
                    <Typography variant="body2" className="font-semibold text-gray-800">
                      {bookingItem.bookingNumber}
                    </Typography>
                  </Box>
                </Box>

                <Box className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <EventIcon className="text-green-500" fontSize="small" />
                  <Box>
                    <Typography variant="caption" className="text-gray-500 font-medium">
                      Scheduled Date
                    </Typography>
                    <Typography variant="body2" className="font-semibold text-gray-800">
                      {moment(bookingItem.scheduledAt).format("MMM DD, YYYY")}
                    </Typography>
                    <Typography variant="caption" className="text-gray-500">
                      {moment(bookingItem.scheduledAt).format("h:mm A")}
                    </Typography>
                  </Box>
                </Box>

                <Box className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <MonetizationOnIcon className="text-yellow-500" fontSize="small" />
                  <Box>
                    <Typography variant="caption" className="text-gray-500 font-medium">
                      Estimated Price
                    </Typography>
                    <Typography variant="h6" className="font-bold text-gray-800">
                      ₹{Math.round(bookingItem.price)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box className="flex flex-wrap gap-3 justify-between items-center pt-6 border-t border-gray-100">
                <Box className="flex gap-2">
                  {bookingItem.status === "CANCELLED" ? (
                    <Button
                      variant="outlined"
                      color="error"
                      disabled
                      startIcon={<DoneIcon />}
                      size="small"
                      className="rounded-full"
                      sx={{ textTransform: 'none' }}
                    >
                      Cancelled
                    </Button>
                  ) : bookingItem.washCreated ? (
                    <Button
                      variant="outlined"
                      color="error"
                      disabled
                      startIcon={<NotInterestedIcon />}
                      size="small"
                      className="rounded-full"
                      sx={{ textTransform: 'none' }}
                    >
                      Wash Created
                    </Button>
                  ) : (
                    <CancellationModal bookingId={bookingItem.bookingId} />
                  )}
                </Box>

                <Box className="flex gap-2">
                  {bookingItem.status !== "CANCELLED" && (
                    <UpdateBookingDialog
                      bookingId={bookingItem.bookingId}
                      vehicle={bookingItem.vehicle.toUpperCase()}
                      washType={bookingItem.type}
                      bookingAt={bookingItem.scheduledAt}
                      address={bookingItem.address}
                    />
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      <Box className="mt-12 flex justify-center">
        <AppPagination
          setPage={setPage}
          totalPages={booking.pagedBooking?.totalPages}
        />
      </Box>
    </div>
  );
}
